import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { postReq } from "../utils/request";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { isPermissionGranted } from "../utils/navPermissions";
import { PagesPermissions } from "../types/Authorization";
import { BugReportStatus } from "../types/BugReport";

/**
 * Count pill for the sidebar's Bug Reports item showing how many reports are
 * still in "New" status. Renders nothing — and never fetches — unless the user
 * holds Configs:read; renders nothing at zero. Refetches on navigation, so
 * triaging a report and leaving the page updates the count without polling.
 */
const NewBugReportsChip: React.FC = () => {
    const [count, setCount] = useState(0);
    const { currentUser } = useUserPermissions();
    const location = useLocation();
    const canRead = isPermissionGranted(currentUser?.pages || [], PagesPermissions.CONFIGS_READ);

    useEffect(() => {
        if (!canRead) return;
        let cancelled = false;
        const fetchCount = async () => {
            const response = await postReq("bug-reports/search", {
                status: BugReportStatus.New,
                page: 1,
                pageSize: 1,
            });
            if (!cancelled && response.status === 200) {
                setCount(response.body?.totalCount ?? 0);
            }
        };
        fetchCount();
        return () => { cancelled = true; };
    }, [canRead, location.pathname]);

    if (!canRead || count === 0) return null;

    return (
        <span className="badge rounded-pill text-bg-danger ms-2" title={`${count} new bug report${count === 1 ? "" : "s"}`}>
            {count > 99 ? "99+" : count}
        </span>
    );
};

export default NewBugReportsChip;
